import { useEffect, useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getImpersonationTargets, getJobTotals, type ImpersonationTarget } from "../lib/api";
import type { JobValueTotals } from "../features/jobs/types";
import { formatMoneyCompact } from "../features/jobs/lib/money";
import { NavMenu } from "./NavMenu";
import navMenuStyles from "./NavMenu.module.css";
import styles from "./AppShell.module.css";

/**
 * Authenticated application layout: top bar with brand, navigation, and the
 * signed-in user's menu. Nested protected routes render inside <Outlet />.
 *
 * The Jobs nav link carries the headline job value totals (drafts / potential /
 * realized). They load once and refresh whenever the board reports a change
 * (create, edit, or a persisted move) via the "afs:jobs-changed" window event.
 */
export function AppShell() {
  const {
    user,
    isMasterAdmin,
    isImpersonating,
    canManageUsers,
    assumeUser,
    exitImpersonation,
    logout,
  } = useAuth();
  const [totals, setTotals] = useState<JobValueTotals | null>(null);
  const [switchTargets, setSwitchTargets] = useState<ImpersonationTarget[]>([]);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const canReadJobs = user?.permissions.includes("*") || user?.permissions.includes("jobs.read");

  useEffect(() => {
    if (!canReadJobs) { setTotals(null); return; }
    let cancelled = false;
    const load = (): void => {
      getJobTotals()
        .then((data) => {
          if (!cancelled) setTotals(data.totals);
        })
        .catch((error) => {
          console.error("Failed to load job totals", error);
        });
    };
    load();
    window.addEventListener("afs:jobs-changed", load);
    return () => {
      cancelled = true;
      window.removeEventListener("afs:jobs-changed", load);
    };
  }, [canReadJobs]);

  useEffect(() => {
    if (!isMasterAdmin) return;
    let cancelled = false;
    getImpersonationTargets()
      .then(({ users }) => {
        if (!cancelled) setSwitchTargets(users);
      })
      .catch((error) => {
        if (!cancelled) {
          setSwitchError(error instanceof Error ? error.message : "Could not load users");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isMasterAdmin]);

  const handleLogout = async (): Promise<void> => {
    await logout();
    window.location.assign("/login");
  };

  const handleAssumeUser = async (userId: string): Promise<void> => {
    if (!userId || switchBusy) return;
    setSwitchBusy(true);
    setSwitchError(null);
    try {
      await assumeUser(userId);
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : "Could not switch user");
      setSwitchBusy(false);
    }
  };

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <img className={styles.logoHeader} src="../../assets/image007.png" alt="AFS Jobs" />
        </div>
        <nav className={styles.nav}>
          {canReadJobs && <>
          <Link to="/jobs" className={styles.navLink}>
            Jobs
            {totals && (
              <span
                className={styles.navTotals}
                title="Drafts / potential / realized — GST-inclusive where a job is scoped, otherwise its price estimate"
              >
                {formatMoneyCompact(totals.drafts)} / {formatMoneyCompact(totals.potential)} /{" "}
                {formatMoneyCompact(totals.realized)}
              </span>
            )}
          </Link>
          <Link to="/schedule" className={styles.navLink}>Schedule</Link>
          <NavMenu label="Money">
            <Link to="/invoices" className={navMenuStyles.item}>
              Invoices
              <span className={navMenuStyles.itemMeta}>Every invoice and quote</span>
            </Link>
          </NavMenu>
          </>}
          {!canReadJobs && user?.systemRole !== "client" && <Link to="/account" className={styles.navLink}>Your account</Link>}
          {canManageUsers && (
            <Link to="/clients" className={styles.navLink}>
              Clients
            </Link>
          )}
          {canManageUsers && (
            <Link to="/admin/users" className={styles.navLink}>
              Users
            </Link>
          )}
          {canManageUsers && (
            <Link to="/admin/email-log" className={styles.navLink}>
              Email log
            </Link>
          )}
          {canManageUsers && (
            <Link to="/admin/email-templates" className={styles.navLink}>
              Templates
            </Link>
          )}
          {canManageUsers && (
            <Link to="/admin/documents" className={styles.navLink}>
              Documents
            </Link>
          )}
        </nav>
        <div className={styles.user}>
          {isMasterAdmin && (
            <label className={styles.switcherLabel}>
              <span className={styles.switcherText}>View as</span>
              <select
                className={styles.switcher}
                value=""
                disabled={switchBusy}
                onChange={(event) => void handleAssumeUser(event.target.value)}
              >
                <option value="">Select user…</option>
                {switchTargets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.displayName || target.email} — {target.email}
                  </option>
                ))}
              </select>
            </label>
          )}
          {switchError && (
            <span className={styles.switchError} role="alert">
              {switchError}
            </span>
          )}
          <span className={styles.userName}>{user?.displayName ?? user?.email}</span>
          {isImpersonating ? (
            <button
              type="button"
              className={styles.exitImpersonation}
              onClick={() => void exitImpersonation()}
            >
              Exit to admin
            </button>
          ) : (
            <button type="button" className={styles.signOut} onClick={handleLogout}>
              Sign out
            </button>
          )}
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}
